import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { scrollToElement } from '@sparefoot/react-core/utils/scrolling';

import { Link } from 'components/core/Link';
import { Review } from 'components/review/Review';
import usePrevious from 'hooks/usePrevious';

import './ReviewList.scss';

// Medium Padding + Sticky Search Height
const scrollOffset = 16 + 82;

export function ReviewList({
	reviews = [],
	facility,
	pageSize = 3,
	currentPage = 1,
}) {
	const prevPage = usePrevious(currentPage);

	useEffect(() => {
		if (prevPage !== currentPage) {
			scrollToElement('.facility-reviews', {
				offset: -scrollOffset,
				behavior: 'smooth',
			});
		}
	}, [currentPage, prevPage]);

	const visibleReviews = useMemo(
		() =>
			reviews
				.slice(pageSize * (currentPage - 1), pageSize * currentPage)
				.map((review) => (
					<Review
						key={review.id}
						review={review}
						facilityName={facility.name}
					/>
				)),
		[reviews, facility, pageSize, currentPage],
	);

	const prevUrl = useMemo(
		() =>
			currentPage - 1 >= 1
				? `${facility.url}?reviewPage=${currentPage - 1}`
				: null,
		[currentPage, facility],
	);

	const nextUrl = useMemo(
		() =>
			currentPage + 1 <= Math.ceil(reviews.length / pageSize)
				? `${facility.url}?reviewPage=${currentPage + 1}`
				: null,
		[currentPage, reviews, facility, pageSize],
	);

	return (
		<div className="review-list">
			<div className="reviews">{visibleReviews}</div>
			<div className="pagination">
				<Link
					className={classnames('previous', {
						disabled: !prevUrl,
					})}
					to={prevUrl}
					rel="nofollow"
				>
					Newer
				</Link>
				<Link
					className={classnames('next', {
						disabled: !nextUrl,
					})}
					to={nextUrl}
					rel="nofollow"
				>
					Older
				</Link>
			</div>
		</div>
	);
}

ReviewList.propTypes = {
	reviews: PropTypes.arrayOf(PropTypes.object),
	facility: PropTypes.shape({
		name: PropTypes.string,
		url: PropTypes.string,
	}),
	pageSize: PropTypes.number,
	currentPage: PropTypes.number,
};

export default ReviewList;
